import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import {
  isRecurring,
  makeOccurrenceDateMatcher,
  getCanonicalKeyForScheduledKey,
} from "../utils/recurringOccurrenceUtils";
import {
  fetchDailyLaborDays,
  summarizeDailyLabor,
  usersToMap,
  getHourlyPayRate,
  UNASSIGNED_JOB,
} from "../utils/techDailyPay";

/**
 * AccountingExports.js  (Priority Pest Defense)
 * Read-only accounting master report.
 *
 * This page NEVER writes to Firebase. It pulls every Priority money collection
 * into one Excel workbook so you can review:
 *   - money received / money paid out
 *   - who owes you / who you owe
 *   - potential money coming in (open proposals, one-time jobs not yet
 *     invoiced, recurring visits not yet invoiced)
 *   - job profitability (actual Tech Daily Pay per job when recorded,
 *     otherwise the job's estimated labor)
 *   - Tech Daily Pay (one row per tech per day)
 *   - reconciliation warnings
 *
 * NO DOUBLE COUNTING - the rules this page follows:
 *   Money Received
 *     - every deposit counts once (Deposits page, Approved Invoices "Paid",
 *       Job Estimate checks).
 *     - transactions income rows that are only a MIRROR of a deposit
 *       (sourceCollection "deposits") are skipped.
 *     - a bank-statement income row is skipped when a deposit was already
 *       created from that same bank line.
 *     - bank lines marked "do not count" (transfers, owner money, matched
 *       existing, corrections) are never counted.
 *     - bulk_income counts once; job "payments" sub-records are skipped when
 *       the same WO / amount / date is already a deposit.
 *   Money Paid Out
 *     - transactions expense rows count (that is where Deposits / Expenses /
 *       Bank Statement / Job Estimate sub pay land).
 *     - employeePay, subcontractorPay, subcontractorPayments, monthlyBills and
 *       bankDeductions count ONLY when they are not already mirrored into
 *       transactions.
 *     - pay/bill records that are still unpaid (status Open/Unpaid/Pending or
 *       dated after today) go to "Who I Owe", not Money Paid Out.
 *     - monthly_expenses (scheduled overhead) count for a month only when no
 *       real payment of the same vendor + amount was recorded that month.
 *     - Daily Spending counts only for months that have no Bank Statement
 *       entries (once the bank statement is in, it replaces the daily log).
 *     - Tech Daily Pay is NOT money paid out. It is labor EARNED. Actual
 *       payroll payments come from Employee Pay / bank payroll lines. The
 *       report shows the two side by side so you can reconcile them.
 *   Who Owes Me
 *     - Recurring jobs reuse one WO for every visit, so deposits are pooled
 *       by invoice number and applied oldest invoice first.
 *   Potential Income
 *     - only work NOT invoiced yet (invoiced money is already in Who Owes Me).
 */

const COLLECTIONS = {
  users: "users",
  jobs: "jobs",
  approvedInvoices: "approvedInvoices",
  deposits: "deposits",
  transactions: "transactions",
  bankStatementEntries: "bankStatementEntries",
  proposals: "proposals",
  jobTotals: "job_totals",
  employeePay: "employeePay",
  subcontractorPay: "subcontractorPay",
  subcontractorPayments: "subcontractorPayments",
  monthlyBills: "monthlyBills",
  bankDeductions: "bankDeductions",
  bulkMaterialExpenses: "bulk_material_expenses",
  monthlyExpenses: "monthly_expenses",
  bulkIncome: "bulk_income",
  dailySpending: "dailySpending",
};

const CLOSED_JOB_STATUSES = new Set(["closed", "canceled", "cancelled", "void", "deleted"]);
const VOID_INVOICE_STATUSES = new Set(["void", "canceled", "cancelled", "deleted"]);
const OPEN_RECORD_STATUSES = new Set(["open", "unpaid", "due", "pending", "scheduled", "not paid", "owed"]);
const DO_NOT_COUNT_ACTIONS = new Set([
  "matched_existing_do_not_count",
  "credit_card_payment_transfer",
  "internal_transfer_do_not_count",
  "owner_contribution_do_not_count",
  "owner_draw_personal_do_not_count",
  "bank_correction_do_not_count",
]);

/* ------------------------------------------------------------------ dates */

const pad = (n) => String(n).padStart(2, "0");
const localKey = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = () => localKey(new Date());
const addDaysKey = (key, days) => {
  const [y, m, d] = String(key).split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  date.setDate(date.getDate() + days);
  return localKey(date);
};

const toDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const mdy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdy) return `${mdy[3]}-${pad(mdy[1])}-${pad(mdy[2])}`;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "" : localKey(parsed);
  }
  if (typeof value?.toDate === "function") return localKey(value.toDate());
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "" : localKey(value);
  if (typeof value === "object" && value.seconds != null) return localKey(new Date(value.seconds * 1000));
  if (typeof value === "number") return localKey(new Date(value));
  return "";
};

const daysBetween = (fromDate, toDate = todayKey()) => {
  if (!fromDate) return "";
  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return Math.max(0, Math.round((end - start) / 86400000));
};

/* ------------------------------------------------------------------ money */

const toNum = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Use this only when a field is supposed to be a real money amount. It rejects
// free-text price boxes like "$3,500 + $1,200" so those digits do not get
// smashed into fake totals.
const toMoneyNumStrict = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).trim().replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned)) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const firstMoneyValue = (...values) => {
  for (const value of values) {
    const amount = toMoneyNumStrict(value);
    if (amount) return amount;
  }
  return 0;
};

const round2 = (value) => Math.round(toNum(value) * 100) / 100;
const money = (value) => toNum(value).toFixed(2);
const displayMoney = (value) =>
  toNum(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
const summarize = (rows, amountKey = "Amount") => rows.reduce((sum, row) => sum + toNum(row[amountKey]), 0);

/* ------------------------------------------------------------------ records */

const firstValue = (...values) => values.find((v) => v !== undefined && v !== null && v !== "") || "";
const normalizeKey = (value) => String(value ?? "").trim();
const lower = (value) => String(value || "").trim().toLowerCase();

// "123-2026-01-05", "123__2026-01-05", "WO-123-2" -> base WO
const getBaseWO = (wo) => {
  let text = normalizeKey(wo);
  if (!text) return "";
  text = text.replace(/(__|-)\d{4}-\d{2}-\d{2}$/, "");
  if (/^\d+-\d+$/.test(text)) return text.replace(/-\d+$/, "");
  if (/^[A-Za-z]+-\d+-\d+$/.test(text)) return text.replace(/-\d+$/, "");
  return text;
};

// Same rule as ApprovedInvoices.getInvoiceNumber: wo first, then invoiceNumber.
const invoiceNumber = (invoice = {}) => normalizeKey(invoice.wo || invoice.invoiceNumber || invoice.id);
const depositInvoiceNumber = (deposit = {}) => normalizeKey(deposit.invoiceNumber || deposit.wo || deposit.workOrder);
const jobNumber = (job = {}) => normalizeKey(job.wo || job.workOrder || job.id);
const baseJobId = (id) => String(id || "").split("__")[0];

const siteName = (item = {}) =>
  firstValue(item.siteName, item.propertyName, item.property, item.customer, item.customerName, item.management, "");

const description = (item = {}) =>
  firstValue(item.scope, item.description, item.jobDescription, item.memo, item.note, item.notes, "");

const statementDetails = (item = {}) =>
  [
    item.statementSection,
    item.referenceNumber ? `Ref ${item.referenceNumber}` : "",
    item.statementPeriod ? `Period ${item.statementPeriod}` : "",
    item.matchNote,
  ]
    .filter(Boolean)
    .join(" • ");

const getDate = (item = {}) =>
  toDateKey(
    firstValue(
      item.date,
      item.datePaid,
      item.paidDate,
      item.invoiceDate,
      item.dateOfService,
      item.serviceDate,
      item.startDate,
      item.submittedDate,
      item.purchaseDate,
      item.billDate,
      item.dueDate,
      item.createdAt,
      item.updatedAt
    )
  );

const itemAmount = (item = {}) => toNum(item.amount ?? item.amountPaid ?? item.total ?? item.cost);

const serviceRowsTotal = (invoice = {}) =>
  (Array.isArray(invoice.serviceRows) ? invoice.serviceRows : []).reduce((sum, row) => {
    if (row?.total !== undefined && row?.total !== null && row?.total !== "") return sum + toNum(row.total);
    return sum + toNum(row?.quantity || 1) * toNum(row?.amount);
  }, 0);

const invoiceAmount = (invoice = {}) =>
  firstMoneyValue(invoice.amount, invoice.price, invoice.rate, invoice.total, invoice.invoiceAmount) ||
  round2(serviceRowsTotal(invoice));

const invoiceDateKey = (invoice = {}) =>
  toDateKey(firstValue(invoice.invoiceDate, invoice.dateOfService, invoice.serviceDate, invoice.createdAt));

const invoiceIsPaid = (invoice = {}) => lower(invoice.status) === "paid" || invoice.paid === true;
const invoiceIsVoid = (invoice = {}) => VOID_INVOICE_STATUSES.has(lower(invoice.status));

// A pay/bill record that has not actually been paid yet.
const recordIsUnpaid = (item = {}, today = todayKey()) => {
  const status = lower(item.status);
  if (OPEN_RECORD_STATUSES.has(status)) return true;
  const date = getDate(item);
  return Boolean(date) && date > today;
};

const txDoesNotCount = (tx = {}) => DO_NOT_COUNT_ACTIONS.has(tx.accountingAction);

const jobLabel = (job) => {
  if (!job) return "";
  const wo = job.wo ? `WO ${job.wo}` : "";
  const site = job.siteName || job.propertyName || "";
  return [wo, site].filter(Boolean).join(" - ") || job.id;
};

// Per-visit fee for a recurring job. First visit uses the Initial Fee when set.
const recurringVisitFee = (job = {}, canonicalKey = "") => {
  const override = job.occurrenceOverrides?.[canonicalKey] || {};
  const overrideFee = firstMoneyValue(override.recurringInvoiceAmount, override.estimatedTotal);
  if (overrideFee) return overrideFee;
  const initial = toMoneyNumStrict(job.initialAmount);
  if (initial > 0 && canonicalKey && canonicalKey === toDateKey(job.startDate)) return initial;
  return firstMoneyValue(job.recurringInvoiceAmount, job.monthlyAmount, job.monthlyFee, job.estimatedTotal);
};

/* ------------------------------------------------------------------ recurring monthly_expenses */

const addFrequencyDate = (date, frequency) => {
  const copy = new Date(date);
  const freq = lower(frequency || "Monthly");
  if (freq === "one time" || freq === "one-time" || freq === "once") copy.setFullYear(copy.getFullYear() + 100);
  else if (freq === "weekly") copy.setDate(copy.getDate() + 7);
  else if (freq === "bi-weekly" || freq === "biweekly" || freq === "every 2 weeks") copy.setDate(copy.getDate() + 14);
  else if (freq === "quarterly") copy.setMonth(copy.getMonth() + 3);
  else if (freq === "yearly" || freq === "annual" || freq === "annually") copy.setFullYear(copy.getFullYear() + 1);
  else copy.setMonth(copy.getMonth() + 1);
  return copy;
};

const getRecurringExpenseOccurrences = (expense, startKey, endKey) => {
  const amount = toMoneyNumStrict(expense.amount || expense.total || expense.cost);
  const expenseStartKey = toDateKey(expense.startDate || expense.date || expense.billDate || expense.dueDate);
  if (!amount || !expenseStartKey || !startKey || !endKey) return [];

  const expenseEndKey = toDateKey(expense.endDate) || "9999-12-31";
  const activeStartKey = expenseStartKey > startKey ? expenseStartKey : startKey;
  const activeEndKey = expenseEndKey < endKey ? expenseEndKey : endKey;
  if (activeStartKey > activeEndKey) return [];

  const frequency = expense.frequency || "Monthly";
  if (["one time", "one-time", "once"].includes(lower(frequency))) {
    return expenseStartKey >= startKey && expenseStartKey <= endKey
      ? [{ ...expense, occurrenceDate: expenseStartKey, occurrenceAmount: amount }]
      : [];
  }

  const rows = [];
  let current = new Date(`${expenseStartKey}T00:00:00`);
  const finalDate = new Date(`${activeEndKey}T00:00:00`);
  let safety = 0;
  while (current <= finalDate && safety < 1000) {
    const key = localKey(current);
    if (key >= activeStartKey && key <= activeEndKey) {
      rows.push({ ...expense, occurrenceDate: key, occurrenceAmount: amount });
    }
    current = addFrequencyDate(current, frequency);
    safety += 1;
  }
  return rows;
};

/* ------------------------------------------------------------------ workbook */

const downloadWorkbook = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  Object.entries(sheets).forEach(([sheetName, rows]) => {
    const safeRows = rows && rows.length ? rows : [{ Note: "No records found for this date range." }];
    const ws = XLSX.utils.json_to_sheet(safeRows);
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  });
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename);
};

const buildPaySummaryRows = (rows, groupKeys) => {
  const grouped = {};
  rows.forEach((row) => {
    const key = groupKeys.map((groupKey) => row[groupKey] || "").join("||");
    if (!grouped[key]) {
      grouped[key] = {
        ...Object.fromEntries(groupKeys.map((groupKey) => [groupKey, row[groupKey] || ""])),
        "Payment Count": 0,
        "Total Paid": 0,
      };
    }
    grouped[key]["Payment Count"] += 1;
    grouped[key]["Total Paid"] += toNum(row.Amount);
  });
  return Object.values(grouped)
    .map((row) => ({ ...row, "Total Paid": money(row["Total Paid"]) }))
    .sort(
      (a, b) =>
        groupKeys
          .map((groupKey) => String(a[groupKey]).localeCompare(String(b[groupKey]), undefined, { numeric: true }))
          .find((result) => result !== 0) || 0
    );
};

/* ------------------------------------------------------------------ styles */

const S = {
  page: { padding: 20, color: "#0f172a", boxSizing: "border-box", maxWidth: "100%" },
  note: {
    color: "#1e3a8a",
    background: "#eff6ff",
    border: "1px solid #93c5fd",
    borderRadius: 10,
    padding: 12,
    margin: "12px 0 18px",
    lineHeight: 1.5,
    fontSize: 14,
  },
  controls: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-end",
    marginBottom: 20,
    padding: 16,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#f8fafc",
  },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, fontWeight: 600, color: "#334155" },
  input: { padding: 8, border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, background: "#fff" },
  btn: (bg, disabled) => ({
    background: disabled ? "#9ca3af" : bg,
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: 150,
  }),
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 20 },
  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 },
  tableWrap: { overflowX: "auto", marginBottom: 20 },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", fontSize: 14 },
  th: { textAlign: "left", padding: "8px 10px", background: "#f1f5f9", borderBottom: "1px solid #e2e8f0" },
  td: { padding: "8px 10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" },
};

const Spinner = ({ color = "#2563eb" }) => (
  <span
    aria-hidden="true"
    style={{
      width: 14,
      height: 14,
      border: "2px solid #cbd5e1",
      borderTopColor: color,
      borderRadius: "50%",
      display: "inline-block",
      animation: "acct-export-spin 0.8s linear infinite",
    }}
  />
);

/* ================================================================== page */

export default function AccountingExports() {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [startDate, setStartDate] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState("");
  const [lookAheadDate, setLookAheadDate] = useState(() => addDaysKey(todayKey(), 30));
  const [data, setData] = useState({});

  const loadData = useCallback(async () => {
    const loadCollectionSafe = async (name) => {
      try {
        const snap = await getDocs(collection(db, name));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.warn(`Could not load ${name}`, err);
        return [];
      }
    };

    const loadJobSubcollection = async (jobs, subcollectionName) => {
      const nested = await Promise.all(
        jobs.map(async (job) => {
          try {
            const snap = await getDocs(collection(db, "jobs", job.id, subcollectionName));
            return snap.docs.map((d) => ({
              id: d.id,
              jobId: job.id,
              wo: job.wo || "",
              siteName: job.siteName || job.propertyName || "",
              sourceJobStatus: job.status || "",
              ...d.data(),
            }));
          } catch (err) {
            return [];
          }
        })
      );
      return nested.flat();
    };

    setLoading(true);
    setLoadError("");
    try {
      const entries = await Promise.all(
        Object.entries(COLLECTIONS).map(async ([key, collectionName]) => [key, await loadCollectionSafe(collectionName)])
      );
      const loaded = Object.fromEntries(entries);
      const [jobPayments, jobExpenses, laborDays] = await Promise.all([
        loadJobSubcollection(loaded.jobs || [], "payments"),
        loadJobSubcollection(loaded.jobs || [], "expenses"),
        fetchDailyLaborDays().catch((err) => {
          console.warn("Could not load Tech Daily Pay", err);
          return [];
        }),
      ]);
      loaded.jobPayments = jobPayments;
      loaded.jobExpenses = jobExpenses;
      loaded.laborDays = laborDays;
      setData(loaded);
    } catch (err) {
      console.error("Accounting export load failed", err);
      setLoadError("Some accounting data could not be loaded. Try Refresh Data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const today = todayKey();
  const effectiveEndDate = endDate || today;
  const effectiveLookAhead = lookAheadDate && lookAheadDate > today ? lookAheadDate : today;

  const users = data.users || [];
  const jobs = data.jobs || [];
  const approvedInvoices = data.approvedInvoices || [];
  const deposits = data.deposits || [];
  const transactions = data.transactions || [];
  const proposals = data.proposals || [];
  const jobTotals = data.jobTotals || [];
  const employeePay = data.employeePay || [];
  const subcontractorPay = data.subcontractorPay || [];
  const subcontractorPayments = data.subcontractorPayments || [];
  const monthlyBills = data.monthlyBills || [];
  const bankDeductions = data.bankDeductions || [];
  const bulkMaterialExpenses = data.bulkMaterialExpenses || [];
  const monthlyExpenses = data.monthlyExpenses || [];
  const bulkIncome = data.bulkIncome || [];
  const dailySpending = data.dailySpending || [];
  const jobPayments = data.jobPayments || [];
  const jobExpenses = data.jobExpenses || [];
  const bankStatementEntries = data.bankStatementEntries || [];
  const laborDays = data.laborDays || [];

  const inRange = useCallback(
    (key) => Boolean(key) && key >= startDate && key <= effectiveEndDate,
    [startDate, effectiveEndDate]
  );
  const inDateRange = useCallback((item) => inRange(getDate(item)), [inRange]);

  /* ---------------------------------------------------------- lookup maps */
  const maps = useMemo(() => {
    const jobById = {};
    const jobByWO = {};
    const invoiceByNumber = {};
    const invoiceKeys = new Set();
    const invoicesByKey = {};
    const invoicesByJobId = {};
    const jobTotalsByWO = {};
    const txSourceKeys = new Set();
    const txIds = new Set();
    const depositSourceIds = new Set();

    jobs.forEach((job) => {
      jobById[job.id] = job;
      const wo = jobNumber(job);
      if (wo) jobByWO[wo] = job;
      const base = getBaseWO(wo);
      if (base && !jobByWO[base]) jobByWO[base] = job;
    });

    approvedInvoices.forEach((invoice) => {
      if (invoiceIsVoid(invoice)) return;
      const key = invoiceNumber(invoice);
      if (!key) return;
      invoiceKeys.add(key);
      invoiceByNumber[key] = invoice;
      if (invoice.invoiceNumber) invoiceByNumber[normalizeKey(invoice.invoiceNumber)] = invoiceByNumber[normalizeKey(invoice.invoiceNumber)] || invoice;
      invoicesByKey[key] = invoicesByKey[key] || [];
      invoicesByKey[key].push(invoice);

      const job =
        jobById[invoice.jobId] ||
        jobById[invoice.originalJobId] ||
        jobByWO[key] ||
        jobByWO[getBaseWO(key)];
      if (job) {
        invoicesByJobId[job.id] = invoicesByJobId[job.id] || [];
        invoicesByJobId[job.id].push(invoice);
      }
    });

    // Deposits for a recurring visit may carry "WO-YYYY-MM-DD"; pool them
    // with the invoice number they belong to.
    const resolveInvoiceKey = (rawKey) => {
      const key = normalizeKey(rawKey);
      if (!key) return "";
      if (invoiceKeys.has(key)) return key;
      const viaNumber = invoiceByNumber[key];
      if (viaNumber) return invoiceNumber(viaNumber);
      const base = getBaseWO(key);
      if (base && invoiceKeys.has(base)) return base;
      return key;
    };

    const depositsByInvoice = {};
    const depositsByJobId = {};
    const jobForDeposit = (deposit) => {
      if (deposit.jobId && jobById[deposit.jobId]) return jobById[deposit.jobId];
      const key = resolveInvoiceKey(depositInvoiceNumber(deposit));
      if (!key) return null;
      const invoice = invoiceByNumber[key];
      if (invoice) {
        const viaInvoice = jobById[invoice.jobId] || jobById[invoice.originalJobId];
        if (viaInvoice) return viaInvoice;
      }
      return jobByWO[key] || jobByWO[getBaseWO(key)] || null;
    };

    deposits.forEach((deposit) => {
      if (deposit.sourceId && (deposit.sourceCollection === "bankStatementEntries" || deposit.source === "bankStatementEntries")) {
        depositSourceIds.add(String(deposit.sourceId));
      }
      const key = resolveInvoiceKey(depositInvoiceNumber(deposit));
      if (key) {
        depositsByInvoice[key] = depositsByInvoice[key] || [];
        depositsByInvoice[key].push(deposit);
      }
      const job = jobForDeposit(deposit);
      if (job) {
        depositsByJobId[job.id] = depositsByJobId[job.id] || [];
        depositsByJobId[job.id].push(deposit);
      }
    });

    jobTotals.forEach((total) => {
      const key = normalizeKey(total.wo || total.id);
      if (key) jobTotalsByWO[key] = total;
    });

    transactions.forEach((tx) => {
      txIds.add(tx.id);
      if (tx.sourceCollection && tx.sourceId) txSourceKeys.add(`${tx.sourceCollection}:${tx.sourceId}`);
    });

    const isMirrored = (collectionName, item) =>
      txSourceKeys.has(`${collectionName}:${item.id}`) || (item.txId && txIds.has(item.txId));

    return {
      jobById,
      jobByWO,
      invoiceByNumber,
      invoiceKeys,
      invoicesByKey,
      invoicesByJobId,
      depositsByInvoice,
      depositsByJobId,
      jobTotalsByWO,
      txSourceKeys,
      depositSourceIds,
      resolveInvoiceKey,
      jobForDeposit,
      isMirrored,
    };
  }, [jobs, deposits, approvedInvoices, jobTotals, transactions]);

  /* ---------------------------------------------------------- tech daily pay */
  const usersById = useMemo(() => usersToMap(users), [users]);

  const laborRange = useMemo(
    () => summarizeDailyLabor(laborDays, usersById, startDate, effectiveEndDate),
    [laborDays, usersById, startDate, effectiveEndDate]
  );

  // Job profitability is all-time (like deposits), so it uses all-time labor.
  const laborAllTime = useMemo(() => summarizeDailyLabor(laborDays, usersById), [laborDays, usersById]);

  const laborByBaseJob = useMemo(() => {
    const out = {};
    Object.entries(laborAllTime.byJob).forEach(([jobId, amount]) => {
      if (jobId === UNASSIGNED_JOB) return;
      const id = baseJobId(jobId);
      out[id] = round2((out[id] || 0) + amount);
    });
    return out;
  }, [laborAllTime]);

  const describeLaborJobs = useCallback(
    (jobIds = []) =>
      (jobIds.length ? jobIds : [])
        .map((id) => {
          const job = maps.jobById[baseJobId(id)];
          return job ? jobLabel(job) : `Job not found (${id})`;
        })
        .join("; ") || "Not tied to a job",
    [maps]
  );

  const techDailyPayRows = useMemo(
    () =>
      laborRange.rows
        .map((row) => ({
          Date: row.date,
          Name: row.name || usersById[row.userId]?.email || row.userId,
          Hours: round2(row.hours),
          Pay: money(row.dailyPay),
          "Jobs (WO / Site)": describeLaborJobs(row.jobIds),
          "Hourly Rate (Staff page)": usersById[row.userId] ? money(getHourlyPayRate(usersById[row.userId])) : "",
        }))
        .sort((a, b) => String(a.Date).localeCompare(String(b.Date)) || String(a.Name).localeCompare(String(b.Name))),
    [laborRange, usersById, describeLaborJobs]
  );

  const techDailyPayByPersonRows = useMemo(
    () =>
      Object.entries(laborRange.byUser)
        .map(([userId, entry]) => ({
          Name: entry.name || usersById[userId]?.email || userId,
          "Days Worked": entry.days,
          Hours: round2(entry.hours),
          "Total Pay": money(entry.amount),
        }))
        .sort((a, b) => String(a.Name).localeCompare(String(b.Name))),
    [laborRange, usersById]
  );

  const techDailyPayByJobRows = useMemo(
    () =>
      Object.entries(laborRange.byJob)
        .map(([jobId, amount]) => {
          const job = jobId === UNASSIGNED_JOB ? null : maps.jobById[baseJobId(jobId)];
          return {
            Job: jobId === UNASSIGNED_JOB ? "Not tied to a job" : job ? jobLabel(job) : `Job not found (${jobId})`,
            WO: job?.wo || "",
            "Tech Daily Pay": money(amount),
          };
        })
        .sort((a, b) => toNum(b["Tech Daily Pay"]) - toNum(a["Tech Daily Pay"])),
    [laborRange, maps]
  );

  /* ---------------------------------------------------------- money received */
  const moneyReceivedRows = useMemo(() => {
    const rows = [];

    deposits.filter(inDateRange).forEach((deposit) => {
      const key = maps.resolveInvoiceKey(depositInvoiceNumber(deposit));
      const invoice = maps.invoiceByNumber[key] || {};
      const job = maps.jobForDeposit(deposit) || {};
      rows.push({
        Date: getDate(deposit),
        "Property / Site": siteName(deposit) || siteName(job) || siteName(invoice),
        "Management / Customer": firstValue(invoice.management, job.management, deposit.management, ""),
        "Check #": deposit.checkNumber || "",
        "WO / Invoice #": depositInvoiceNumber(deposit),
        "Paid By / Company": firstValue(deposit.company, deposit.payerName, ""),
        Amount: money(deposit.amount),
        "Source Page": firstValue(deposit.createdFrom, deposit.source, "Deposits"),
        "Matched to Invoice?": maps.invoiceKeys.has(key) ? "Yes" : "No",
        "Matched to Job?": job.id ? "Yes" : "No",
        "Reference #": deposit.referenceNumber || "",
        "Statement Details": statementDetails(deposit),
        Description: description(deposit),
      });
    });

    bulkIncome.filter(inDateRange).forEach((income) => {
      rows.push({
        Date: getDate(income),
        "Property / Site": siteName(income),
        "Management / Customer": income.payerName || "",
        "Check #": income.checkNumber || "",
        "WO / Invoice #": income.wo || "",
        "Paid By / Company": income.payerName || "",
        Amount: money(income.amount),
        "Source Page": "Job Estimate - Bulk Income",
        "Matched to Invoice?": income.wo && maps.invoiceKeys.has(maps.resolveInvoiceKey(income.wo)) ? "Yes" : "No",
        "Matched to Job?": income.jobId || income.matchedToJob ? "Yes" : "No",
        Description: description(income),
      });
    });

    transactions
      .filter((tx) => lower(tx.type) === "income")
      .filter(inDateRange)
      .filter((tx) => tx.sourceCollection !== "deposits") // mirror of a deposit already counted above
      .filter((tx) => !txDoesNotCount(tx))
      .filter((tx) => !(tx.sourceCollection === "bankStatementEntries" && maps.depositSourceIds.has(String(tx.sourceId))))
      .forEach((tx) => {
        const job = maps.jobById[tx.jobId] || {};
        rows.push({
          Date: getDate(tx),
          "Property / Site": siteName(tx) || siteName(job),
          "Management / Customer": firstValue(tx.payer, tx.vendor, tx.customer, ""),
          "Check #": tx.checkNumber || "",
          "WO / Invoice #": firstValue(tx.wo, tx.invoiceNumber, job.wo, ""),
          "Paid By / Company": firstValue(tx.payer, tx.company, ""),
          Amount: money(tx.amount),
          "Source Page": tx.sourceCollection === "bankStatementEntries" ? "Bank Statement Entry" : "Transactions",
          "Matched to Invoice?": tx.wo && maps.invoiceKeys.has(maps.resolveInvoiceKey(tx.wo)) ? "Yes" : "No",
          "Matched to Job?": job.id ? "Yes" : "No",
          "Reference #": tx.referenceNumber || "",
          "Statement Details": statementDetails(tx),
          Description: description(tx),
        });
      });

    jobPayments.filter(inDateRange).forEach((payment) => {
      const key = normalizeKey(payment.wo);
      const alreadyInDeposits = deposits.some(
        (deposit) =>
          maps.resolveInvoiceKey(depositInvoiceNumber(deposit)) === maps.resolveInvoiceKey(key) &&
          toNum(deposit.amount) === toNum(payment.amount) &&
          getDate(deposit) === getDate(payment)
      );
      if (alreadyInDeposits || payment.bulkIncomeId) return;
      rows.push({
        Date: getDate(payment),
        "Property / Site": siteName(payment),
        "Management / Customer": payment.payerName || payment.vendor || "",
        "Check #": payment.checkNumber || "",
        "WO / Invoice #": key,
        "Paid By / Company": payment.payerName || "",
        Amount: money(payment.amount),
        "Source Page": "Job Payment",
        "Matched to Invoice?": key && maps.invoiceKeys.has(maps.resolveInvoiceKey(key)) ? "Yes" : "No",
        "Matched to Job?": payment.jobId ? "Yes" : "No",
        Description: description(payment),
      });
    });

    return rows.sort((a, b) => String(a.Date).localeCompare(String(b.Date)));
  }, [deposits, bulkIncome, transactions, jobPayments, inDateRange, maps]);

  /* ---------------------------------------------------------- money paid out */
  const paidOut = useMemo(() => {
    const rows = [];
    const monthsWithBankStatements = new Set(
      bankStatementEntries.map((entry) => getDate(entry).slice(0, 7)).filter(Boolean)
    );

    const addExpense = (item, sourcePage, vendorField, categoryFallback) => {
      const date = getDate(item);
      if (!inRange(date) || date > today) return;
      const job = maps.jobById[item.jobId] || maps.jobByWO[normalizeKey(item.wo)] || {};
      rows.push({
        Date: date,
        "Paid To / Vendor": firstValue(
          item[vendorField],
          item.vendor,
          item.vendorName,
          item.storeName,
          item.employeeName,
          item.subcontractorName,
          item.bankName,
          item.property,
          ""
        ),
        Category: firstValue(item.category, item.billType, item.deductionType, item.payType, categoryFallback),
        "Property / Job": siteName(item) || siteName(job),
        WO: firstValue(item.wo, job.wo, ""),
        Description: description(item),
        "Reference #": item.referenceNumber || item.reference || "",
        "Statement Details": statementDetails(item),
        Amount: money(itemAmount(item)),
        "Source Page": item.sourceCollection === "bankStatementEntries" ? "Bank Statement Entry" : sourcePage,
        Status: item.status || "",
      });
    };

    transactions
      .filter((tx) => lower(tx.type) === "expense")
      .filter((tx) => !txDoesNotCount(tx))
      .forEach((tx) => addExpense(tx, "Transactions", "vendor", tx.category || "Expense"));

    const addUnmirrored = (list, collectionName, sourcePage, vendorField, categoryFallback) => {
      list.forEach((item) => {
        if (maps.isMirrored(collectionName, item)) return;
        if (recordIsUnpaid(item, today)) return; // goes to Who I Owe instead
        addExpense(item, sourcePage, vendorField, categoryFallback);
      });
    };

    addUnmirrored(employeePay, "employeePay", "Expenses - Employee Pay", "employeeName", "Labor");
    addUnmirrored(subcontractorPay, "subcontractorPay", "Expenses - Subcontractor Pay", "vendor", "Subcontractor");
    addUnmirrored(subcontractorPayments, "subcontractorPayments", "Subcontractor Payments (older)", "subcontractorName", "Subcontractor");
    addUnmirrored(monthlyBills, "monthlyBills", "Expenses - Monthly Bills", "vendor", "Monthly Bill");
    addUnmirrored(bankDeductions, "bankDeductions", "Expenses - Bank Deductions", "bankName", "Bank Fee");

    bulkMaterialExpenses.forEach((x) => addExpense(x, "Job Estimate - Bulk Materials", "storeName", "Materials"));
    jobExpenses.forEach((x) => addExpense(x, "Job Expense", "vendor", "Job Expense"));

    // Real payments recorded so far, for matching scheduled overhead.
    const actualByMonth = {};
    rows.forEach((row) => {
      const month = String(row.Date).slice(0, 7);
      actualByMonth[month] = actualByMonth[month] || [];
      actualByMonth[month].push(row);
    });

    const scheduledRows = [];
    monthlyExpenses.forEach((expense) => {
      getRecurringExpenseOccurrences(expense, startDate, effectiveEndDate).forEach((occurrence) => {
        const date = occurrence.occurrenceDate;
        const vendor = lower(firstValue(expense.vendorName, expense.vendor, expense.description));
        const amount = occurrence.occurrenceAmount;
        const month = date.slice(0, 7);
        const isOwnSourceMonth =
          expense.sourceCollection === "bankStatementEntries" && toDateKey(expense.startDate).slice(0, 7) === month;
        const match = (actualByMonth[month] || []).find((row) => {
          if (Math.abs(toNum(row.Amount) - amount) > 1) return false;
          const rowVendor = lower(row["Paid To / Vendor"]);
          return vendor && rowVendor && (rowVendor.includes(vendor) || vendor.includes(rowVendor));
        });
        const future = date > today;
        const counted = !future && !match && !isOwnSourceMonth;

        scheduledRows.push({
          Date: date,
          Vendor: firstValue(expense.vendorName, expense.vendor, ""),
          "Bill Type": firstValue(expense.billType, "Monthly Expense"),
          Frequency: expense.frequency || "Monthly",
          Amount: money(amount),
          "Counted in Money Paid Out?": counted ? "Yes" : "No",
          Why: future
            ? "Not due yet - shown in Who I Owe"
            : match
            ? `Already paid: ${match["Source Page"]} ${match.Date}`
            : isOwnSourceMonth
            ? "Created from a bank statement line that is already counted"
            : "No real payment found this month - counted as paid",
          Description: description(expense),
        });

        if (counted) {
          addExpense(
            { ...expense, date, amount, category: expense.billType || "Monthly Expense" },
            "Job Estimate - Monthly Expenses",
            "vendorName",
            "Monthly Expense"
          );
        }
      });
    });

    const dailySpendingRows = [];
    dailySpending.forEach((day) => {
      const date = toDateKey(day.date || day.id);
      if (!inRange(date)) return;
      const counted = !monthsWithBankStatements.has(date.slice(0, 7)) && date <= today;
      const lines = [
        ...(Array.isArray(day.creditCards) ? day.creditCards : []).map((x) => ({ ...x, __kind: "Credit Card" })),
        ...(Array.isArray(day.bankAccounts) ? day.bankAccounts : []).map((x) => ({ ...x, __kind: "Bank" })),
      ];
      lines.forEach((line) => {
        const amount = toNum(line.amount);
        if (!amount) return;
        dailySpendingRows.push({
          Date: date,
          Type: line.__kind,
          "Card / Account": line.name || "",
          Amount: money(amount),
          "Counted in Money Paid Out?": counted ? "Yes" : "No",
          Why: counted
            ? "No bank statement entered for this month yet"
            : "This month's bank statement is entered - it replaces the daily log",
        });
        if (counted) {
          addExpense(
            { date, amount, vendor: line.name || line.__kind, category: `Daily Spending - ${line.__kind}` },
            "Daily Spending",
            "vendor",
            "Daily Spending"
          );
        }
      });
    });

    return {
      rows: rows.sort((a, b) => String(a.Date).localeCompare(String(b.Date))),
      scheduledRows: scheduledRows.sort((a, b) => String(a.Date).localeCompare(String(b.Date))),
      dailySpendingRows,
    };
  }, [
    transactions,
    employeePay,
    subcontractorPay,
    subcontractorPayments,
    monthlyBills,
    bankDeductions,
    bulkMaterialExpenses,
    monthlyExpenses,
    dailySpending,
    jobExpenses,
    bankStatementEntries,
    startDate,
    effectiveEndDate,
    inRange,
    today,
    maps,
  ]);
  const moneyPaidRows = paidOut.rows;

  /* ---------------------------------------------------------- pay detail */
  const payrollSubDetailRows = useMemo(() => {
    const rows = [];
    const addPayRow = (item, payGroup, sourcePage, personField, categoryFallback) => {
      const date = getDate(item);
      if (!inRange(date) || date > today) return;
      rows.push({
        Date: date,
        Year: date.slice(0, 4),
        Month: date.slice(0, 7),
        "Pay Group": payGroup,
        "Employee / Sub": firstValue(
          item[personField],
          item.employeeName,
          item.subcontractorName,
          item.vendor,
          item.vendorName,
          item.userName,
          item.property,
          payGroup === "Employee" ? "Unnamed Employee" : "Unnamed Subcontractor"
        ),
        "Pay Type / Category": firstValue(item.payType, item.category, categoryFallback),
        "Property / Job": siteName(item),
        WO: firstValue(item.wo, item.invoiceNumber, ""),
        Description: description(item),
        Amount: money(itemAmount(item)),
        "Source Page": sourcePage,
        Status: item.status || "",
      });
    };

    // Mirrored ledger rows first, then pay docs that were never mirrored.
    transactions
      .filter((tx) => lower(tx.type) === "expense" && !txDoesNotCount(tx))
      .forEach((tx) => {
        if (tx.sourceCollection === "employeePay") addPayRow(tx, "Employee", "Transactions / Employee Pay", "vendor", "Labor");
        if (tx.sourceCollection === "subcontractorPay" || tx.sourceCollection === "subcontractorPayments") {
          addPayRow(tx, "Subcontractor", "Transactions / Subcontractor Pay", "vendor", "Subcontractor");
        }
      });

    const addUnmirrored = (list, collectionName, payGroup, sourcePage, personField, categoryFallback) => {
      list.forEach((item) => {
        if (maps.isMirrored(collectionName, item) || recordIsUnpaid(item, today)) return;
        addPayRow(item, payGroup, sourcePage, personField, categoryFallback);
      });
    };
    addUnmirrored(employeePay, "employeePay", "Employee", "Expenses - Employee Pay", "employeeName", "Labor");
    addUnmirrored(subcontractorPay, "subcontractorPay", "Subcontractor", "Expenses - Subcontractor Pay", "vendor", "Subcontractor");
    addUnmirrored(subcontractorPayments, "subcontractorPayments", "Subcontractor", "Subcontractor Payments (older)", "subcontractorName", "Subcontractor");

    return rows.sort(
      (a, b) =>
        String(a.Date).localeCompare(String(b.Date)) ||
        String(a["Pay Group"]).localeCompare(String(b["Pay Group"])) ||
        String(a["Employee / Sub"]).localeCompare(String(b["Employee / Sub"]))
    );
  }, [transactions, employeePay, subcontractorPay, subcontractorPayments, inRange, today, maps]);

  const payrollSubMonthlySummaryRows = useMemo(
    () => buildPaySummaryRows(payrollSubDetailRows, ["Month", "Pay Group", "Employee / Sub"]),
    [payrollSubDetailRows]
  );
  const payrollSubYearlySummaryRows = useMemo(
    () => buildPaySummaryRows(payrollSubDetailRows, ["Year", "Pay Group", "Employee / Sub"]),
    [payrollSubDetailRows]
  );
  const payrollSubGrandSummaryRows = useMemo(
    () => buildPaySummaryRows(payrollSubDetailRows, ["Pay Group"]),
    [payrollSubDetailRows]
  );

  /* ---------------------------------------------------------- bank statement audit */
  const bankStatementAuditRows = useMemo(
    () =>
      bankStatementEntries
        .filter(inDateRange)
        .map((entry) => {
          const recordOnly =
            entry.matchedExistingPayment || (entry.accountingAction && DO_NOT_COUNT_ACTIONS.has(entry.accountingAction));
          return {
            Date: getDate(entry),
            Type: entry.type === "moneyIn" ? "Money In" : "Money Out",
            Amount: money(entry.amount),
            "Vendor / Payer": firstValue(entry.vendorOrPayer, entry.vendor, entry.payer, ""),
            Category: firstValue(entry.effectiveCategory, entry.category, ""),
            "Other Category": entry.categoryOther || "",
            "WO / Invoice #": firstValue(entry.wo, entry.invoiceNumber, entry.matchedInvoiceNumber, ""),
            "Property / Site": siteName(entry),
            "Bank Account": firstValue(entry.bankAccountLabel, entry.bankName, ""),
            "Reference #": entry.referenceNumber || "",
            "Statement Section": entry.statementSection || "",
            "Statement Period": entry.statementPeriod || "",
            "Matched Existing?": entry.matchedExistingPayment ? "Yes" : "No",
            "Counting Status": recordOnly ? "Recordkeeping only - not counted again" : "Counted (through its Transactions row)",
            "Match Type": entry.matchType || "",
            "Match Note": entry.matchNote || "",
            Description: description(entry),
            "Raw Statement Line": entry.rawLine || "",
          };
        })
        .sort((a, b) => String(a.Date).localeCompare(String(b.Date))),
    [bankStatementEntries, inDateRange]
  );

  /* ---------------------------------------------------------- who owes me */
  const invoiceBalances = useMemo(() => {
    // Apply each invoice number's pooled deposits to its invoices, oldest first.
    const result = {};
    Object.entries(maps.invoicesByKey).forEach(([key, list]) => {
      let remaining = (maps.depositsByInvoice[key] || []).reduce((sum, d) => sum + toNum(d.amount), 0);
      [...list]
        .sort((a, b) => invoiceDateKey(a).localeCompare(invoiceDateKey(b)))
        .forEach((invoice) => {
          const amount = invoiceAmount(invoice);
          const applied = Math.min(amount, Math.max(remaining, 0));
          remaining -= applied;
          const markedPaid = invoiceIsPaid(invoice);
          result[invoice.id] = {
            amount,
            applied: round2(applied),
            balance: markedPaid ? 0 : round2(Math.max(amount - applied, 0)),
            shortfall: markedPaid ? round2(Math.max(amount - applied, 0)) : 0,
          };
        });
      result[`__extra__${key}`] = round2(Math.max(remaining, 0));
    });
    return result;
  }, [maps]);

  const whoOwesMeRows = useMemo(
    () =>
      approvedInvoices
        .filter((invoice) => !invoiceIsVoid(invoice))
        .map((invoice) => {
          const key = invoiceNumber(invoice);
          const info = invoiceBalances[invoice.id] || { amount: invoiceAmount(invoice), applied: 0, balance: invoiceAmount(invoice) };
          const invDate = invoiceDateKey(invoice);
          const shared = (maps.invoicesByKey[key] || []).length > 1;
          return {
            Site: siteName(invoice),
            "Management / Customer": firstValue(invoice.management, invoice.managementCompany, invoice.clientName, ""),
            "Invoice #": key,
            "Invoice Date": invDate,
            "Service Date": toDateKey(firstValue(invoice.dateOfService, invoice.serviceDate)),
            Status: invoice.status || "Draft",
            "Sent?": invoice.sent || invoice.sentAt || invoice.emailedAt || lower(invoice.status) === "sent" ? "Yes" : "No",
            "Invoice Amount": money(info.amount),
            "Amount Paid": money(info.applied),
            "Balance Due": money(info.balance),
            "Days Old": daysBetween(invDate),
            Notes: shared ? "Recurring job: payments for this WO are applied to the oldest visit invoice first." : "",
            Description: description(invoice),
          };
        })
        .filter((row) => toNum(row["Balance Due"]) > 0)
        .sort((a, b) => toNum(b["Balance Due"]) - toNum(a["Balance Due"])),
    [approvedInvoices, invoiceBalances, maps]
  );

  /* ---------------------------------------------------------- who I owe */
  const whoIOweRows = useMemo(() => {
    const rows = [];
    const addPayable = (item, source, vendorField, fallbackCategory, dueKey) => {
      rows.push({
        Vendor: firstValue(
          item[vendorField],
          item.vendor,
          item.vendorName,
          item.subcontractorName,
          item.userName,
          item.employeeName,
          item.bankName,
          item.storeName,
          ""
        ),
        "Bill Type / Category": firstValue(item.billType, item.category, item.payType, item.deductionType, fallbackCategory),
        Description: description(item),
        "Due Date": dueKey,
        "Amount Owed": money(itemAmount(item)),
        Status: item.status || (dueKey > today ? "Scheduled" : "Open"),
        "Linked Job / Site": siteName(item),
        WO: item.wo || "",
        "Source Page": source,
      });
    };

    const addOpenRecords = (list, source, vendorField, fallbackCategory) => {
      list.forEach((item) => {
        if (!recordIsUnpaid(item, today)) return;
        const due = toDateKey(firstValue(item.dueDate, item.date, item.billDate));
        if (due && (due < startDate || due > effectiveLookAhead)) return;
        addPayable(item, source, vendorField, fallbackCategory, due);
      });
    };

    addOpenRecords(monthlyBills, "Expenses - Monthly Bills", "vendor", "Monthly Bill");
    addOpenRecords(employeePay, "Expenses - Employee Pay", "employeeName", "Labor");
    addOpenRecords(subcontractorPay, "Expenses - Subcontractor Pay", "vendor", "Subcontractor");
    addOpenRecords(subcontractorPayments, "Subcontractor Payments (older)", "subcontractorName", "Subcontractor");

    // Upcoming scheduled overhead (past ones are treated as paid in Money Paid Out).
    monthlyExpenses.forEach((expense) => {
      getRecurringExpenseOccurrences(expense, addDaysKey(today, 1), effectiveLookAhead).forEach((occurrence) => {
        addPayable(
          { ...occurrence, amount: occurrence.occurrenceAmount },
          "Job Estimate - Monthly Expenses",
          "vendorName",
          "Monthly Expense",
          occurrence.occurrenceDate
        );
      });
    });

    return rows.sort((a, b) => String(a["Due Date"]).localeCompare(String(b["Due Date"])));
  }, [monthlyBills, employeePay, subcontractorPay, subcontractorPayments, monthlyExpenses, startDate, effectiveLookAhead, today]);

  /* ---------------------------------------------------------- recurring visit state */
  const recurringVisitInfo = useMemo(() => {
    const info = {};
    jobs.filter(isRecurring).forEach((job) => {
      const invoiceKeysForJob = (maps.invoicesByJobId[job.id] || []).map((inv) => toDateKey(inv.occurrenceKey)).filter(Boolean);
      const isInvoiced = makeOccurrenceDateMatcher(job, [
        ...(job.invoicedDates || []),
        ...Object.keys(job.approvedInvoiceIdByDate || {}),
        ...invoiceKeysForJob,
      ]);
      const isCompleted = makeOccurrenceDateMatcher(job, job.completedDates || []);
      const isExcluded = makeOccurrenceDateMatcher(job, job.excludedDates || []);

      // Completed visits, folded back to their canonical date.
      const completedKeys = new Set(
        (job.completedDates || [])
          .map(toDateKey)
          .filter(Boolean)
          .map((key) => getCanonicalKeyForScheduledKey(job, key) || key)
      );
      info[job.id] = { isInvoiced, isCompleted, isExcluded, completedKeys };
    });
    return info;
  }, [jobs, maps]);

  /* ---------------------------------------------------------- potential income */
  const potentialMoneyRows = useMemo(() => {
    const rows = [];
    const jobsByProposalId = new Set(jobs.map((job) => job.proposalId).filter(Boolean));

    proposals.forEach((proposal) => {
      const status = lower(proposal.status);
      if (["not approved", "declined", "rejected", "lost", "void", "canceled", "cancelled"].includes(status)) return;
      if (jobsByProposalId.has(proposal.id) || proposal.jobId) return; // already a job - counted from the job
      const amount = firstMoneyValue(
        proposal.proposalTotal,
        proposal.contractAmount,
        proposal.proposalAmount,
        proposal.approvedAmount,
        proposal.finalAmount,
        proposal.totalAmount,
        proposal.estimatedTotal
      );
      rows.push({
        Source: amount ? "Open Proposal" : "Proposal - Amount Needs Setup",
        Site: siteName(proposal),
        "WO / Proposal #": firstValue(proposal.proposalID, proposal.proposalId, proposal.id),
        Status: proposal.status || "Proposal",
        Scope: description(proposal),
        Visits: "",
        Amount: amount ? money(amount) : "",
        "Expected Date": toDateKey(firstValue(proposal.submittedDate, proposal.createdAt)),
        Notes: amount
          ? "Proposal total (not a job yet)."
          : "Not counted: this proposal has no numeric Proposal Total.",
      });
    });

    jobs.forEach((job) => {
      if (CLOSED_JOB_STATUSES.has(lower(job.status))) return;
      if (job.isWarranty === true) return;
      const key = jobNumber(job);

      if (isRecurring(job)) {
        const visit = recurringVisitInfo[job.id];
        if (!visit || job.seriesEndedAt) return;
        const dates = getOccurrencesForJob(job, startDate, effectiveLookAhead).filter(
          (dateKey) => !visit.isExcluded(dateKey) && !visit.isInvoiced(dateKey)
        );
        const done = dates.filter((dateKey) => dateKey <= today && visit.isCompleted(dateKey));
        const upcoming = dates.filter((dateKey) => dateKey > today || (dateKey === today && !visit.isCompleted(dateKey)));
        const pushRows = (list, source, note) => {
          if (!list.length) return;
          const amount = list.reduce((sum, dateKey) => sum + recurringVisitFee(job, dateKey), 0);
          if (!amount) return;
          rows.push({
            Source: source,
            Site: siteName(job),
            "WO / Proposal #": key,
            Status: job.status || "",
            Scope: description(job) || job.serviceType || "",
            Visits: `${list.length} (${list[0]}${list.length > 1 ? ` to ${list[list.length - 1]}` : ""})`,
            Amount: money(amount),
            "Expected Date": list[0],
            Notes: note,
          });
        };
        pushRows(done, "Recurring - Done, Not Invoiced", "Visits completed but no invoice yet. Create the invoice from Send Invoices.");
        pushRows(upcoming, "Recurring - Upcoming Visits", `Scheduled visits through ${effectiveLookAhead} at the per-visit fee.`);
        return;
      }

      // One-time job: invoiced money is already in Who Owes Me.
      if ((maps.invoicesByJobId[job.id] || []).length) return;
      const total = firstMoneyValue(job.contractAmount, job.invoiceAmount, job.estimatedTotal, job.price, job.total);
      if (!total) return;
      const paid = (maps.depositsByJobId[job.id] || []).reduce((sum, deposit) => sum + toNum(deposit.amount), 0);
      const balance = Math.max(total - paid, 0);
      if (balance <= 0) return;
      rows.push({
        Source: lower(job.status) === "completed" ? "Job Done, Not Invoiced" : "Job Not Invoiced Yet",
        Site: siteName(job),
        "WO / Proposal #": key,
        Status: job.status || "",
        Scope: description(job),
        Visits: "",
        Amount: money(balance),
        "Expected Date": toDateKey(firstValue(job.startDate, job.endDate, job.createdAt)),
        Notes: "No invoice found for this job yet.",
      });
    });

    return rows.sort((a, b) => toNum(b.Amount) - toNum(a.Amount));
  }, [proposals, jobs, maps, recurringVisitInfo, startDate, effectiveLookAhead, today]);

  /* ---------------------------------------------------------- job profitability */
  const jobProfitabilityRows = useMemo(() => {
    const bulkIncomeByJob = {};
    bulkIncome.forEach((x) => {
      if (!x.jobId) return;
      bulkIncomeByJob[x.jobId] = (bulkIncomeByJob[x.jobId] || 0) + toNum(x.amount);
    });
    const subPayByJob = {};
    [...subcontractorPay, ...subcontractorPayments].forEach((x) => {
      const jobId = baseJobId(x.jobId);
      if (!jobId) return;
      subPayByJob[jobId] = (subPayByJob[jobId] || 0) + toNum(x.amount ?? x.amountPaid);
    });
    const ledgerExpenseByJob = {};
    transactions.forEach((tx) => {
      if (lower(tx.type) !== "expense" || !tx.jobId || txDoesNotCount(tx)) return;
      if (["subcontractorPay", "subcontractorPayments", "employeePay"].includes(tx.sourceCollection)) return; // sub pay / labor counted elsewhere
      if (tx.sourceCollection === "bankStatementEntries" && lower(tx.category).includes("material")) return; // already in job materials
      const jobId = baseJobId(tx.jobId);
      ledgerExpenseByJob[jobId] = (ledgerExpenseByJob[jobId] || 0) + toNum(tx.amount);
    });

    return jobs
      .map((job) => {
        const key = jobNumber(job);
        const recurring = isRecurring(job);
        const jobInvoices = (maps.invoicesByJobId[job.id] || []).filter((inv) => !invoiceIsVoid(inv));
        const invoicedTotal = jobInvoices.reduce((sum, inv) => sum + invoiceAmount(inv), 0);

        const depositsTotal = (maps.depositsByJobId[job.id] || []).reduce((sum, deposit) => sum + toNum(deposit.amount), 0);
        const extraPayments = jobPayments
          .filter((payment) => payment.jobId === job.id && !payment.bulkIncomeId)
          .filter(
            (payment) =>
              !(maps.depositsByJobId[job.id] || []).some(
                (deposit) => toNum(deposit.amount) === toNum(payment.amount) && getDate(deposit) === getDate(payment)
              )
          )
          .reduce((sum, payment) => sum + toNum(payment.amount), 0);
        const received = depositsTotal + toNum(bulkIncomeByJob[job.id]) + extraPayments;

        const contractAmount = recurring
          ? invoicedTotal
          : firstMoneyValue(job.contractAmount, job.invoiceAmount) || invoicedTotal || firstMoneyValue(job.estimatedTotal, job.price, job.total);

        const receiptsTotal = Array.isArray(job.receipts)
          ? job.receipts.reduce((sum, r) => sum + toNum(r.amount), 0)
          : toNum(job.receiptsTotal);
        const estimatedMaterial = toNum(job.estimatedMaterial || job.materialsCost);

        const subPayTotal = subPayByJob[job.id]
          ? subPayByJob[job.id]
          : Array.isArray(job.subPay)
          ? job.subPay.reduce((sum, r) => sum + toNum(r.amount), 0)
          : toNum(job.subPayTotal || job.subPay);

        const jobExpenseTotal = jobExpenses
          .filter((expense) => expense.jobId === job.id)
          .reduce((sum, expense) => sum + toNum(expense.amount), 0);

        // Actual Tech Daily Pay wins; the estimate is only a fallback. Never both.
        const actualLabor = toNum(laborByBaseJob[job.id]);
        const estimatedLabor = toNum(job.estimatedLaborCost || job.laborCost || job.__fin?.laborCost);
        const labor = actualLabor > 0 ? actualLabor : estimatedLabor;
        const laborSource = actualLabor > 0 ? "Tech Daily Pay (actual)" : estimatedLabor > 0 ? "Estimate (no daily pay recorded)" : "";

        const otherExpenses = toNum(job.miscCost) + jobExpenseTotal + toNum(ledgerExpenseByJob[job.id]);
        const totalCost = estimatedMaterial + receiptsTotal + labor + subPayTotal + otherExpenses;
        const isWarranty = job.isWarranty === true;
        const visit = recurringVisitInfo[job.id];

        return {
          WO: key,
          Site: siteName(job),
          Type: recurring ? "Recurring" : "One-time",
          Status: job.status || "",
          Warranty: isWarranty ? "Yes" : "No",
          "Visits Completed": recurring ? (visit?.completedKeys.size || 0) : "",
          "Contract / Invoiced Amount": money(isWarranty ? 0 : contractAmount),
          Received: money(isWarranty ? 0 : received),
          "Estimated Materials": money(estimatedMaterial),
          "Receipt Expenses": money(receiptsTotal),
          Labor: money(labor),
          "Labor Source": laborSource,
          "Estimated Labor (info)": money(estimatedLabor),
          "Sub Pay": money(subPayTotal),
          "Other Expenses": money(otherExpenses),
          "Total Cost": money(totalCost),
          "Warranty Cost": money(isWarranty ? totalCost : 0),
          Profit: money(isWarranty ? 0 : received - totalCost),
          "Balance Due": money(isWarranty ? 0 : Math.max(contractAmount - received, 0)),
          Scope: description(job),
        };
      })
      .sort((a, b) => String(a.WO).localeCompare(String(b.WO), undefined, { numeric: true }));
  }, [jobs, bulkIncome, subcontractorPay, subcontractorPayments, transactions, jobPayments, jobExpenses, laborByBaseJob, recurringVisitInfo, maps]);

  /* ---------------------------------------------------------- reconciliation */
  const reconciliationRows = useMemo(() => {
    const rows = [];
    const addIssue = (severity, issue, reference, site, amount, fix) => {
      rows.push({ Severity: severity, Issue: issue, Reference: reference || "", Site: site || "", Amount: money(amount), "Suggested Fix": fix });
    };

    approvedInvoices.forEach((invoice) => {
      if (invoiceIsVoid(invoice)) return;
      const key = invoiceNumber(invoice);
      const info = invoiceBalances[invoice.id];
      if (!info) return;
      if (info.shortfall > 0.01) {
        addIssue("High", "Invoice marked Paid but deposits are less than the invoice amount", key, siteName(invoice), info.shortfall, "Check the deposit entries, or change the invoice status back to Sent.");
      }
      if (!invoiceIsPaid(invoice) && info.amount > 0 && info.applied >= info.amount) {
        addIssue("Medium", "Invoice looks fully paid by deposits but status is not Paid", key, siteName(invoice), info.applied, "Open Approved Invoices - it syncs Paid status when it loads.");
      }
      if (!info.amount) {
        addIssue("Medium", "Invoice has no amount", key, siteName(invoice), 0, "Open the invoice in Approved Invoices and enter the service rows/amount.");
      }
    });

    Object.keys(maps.invoicesByKey).forEach((key) => {
      const extra = invoiceBalances[`__extra__${key}`];
      if (extra > 0.01) {
        addIssue("Medium", "Deposits for this invoice # are more than everything invoiced", key, siteName(maps.invoicesByKey[key][0]), extra, "Check for a duplicate deposit or a missing invoice.");
      }
    });

    deposits.forEach((deposit) => {
      const rawKey = depositInvoiceNumber(deposit);
      const key = maps.resolveInvoiceKey(rawKey);
      if (key && !maps.invoiceKeys.has(key)) {
        addIssue("Medium", "Deposit has no matching approved invoice", rawKey, siteName(deposit), deposit.amount, "Check the WO / invoice # on the deposit, or create the invoice.");
      }
      if (key && !maps.jobForDeposit(deposit)) {
        addIssue("Low", "Deposit has no matching job", rawKey, siteName(deposit), deposit.amount, "Check the WO number, or confirm this is general income.");
      }
      if (!rawKey) {
        addIssue("Low", "Deposit has no WO / invoice #", deposit.checkNumber || deposit.id, siteName(deposit), deposit.amount, "Add the WO / invoice # on the Deposits page so it pays the right invoice.");
      }
      const ownLedger = deposit.sourceCollection === "bankStatementEntries" || deposit.source === "bankStatementEntries";
      if (deposit.id && !ownLedger && !maps.txSourceKeys.has(`deposits:${deposit.id}`)) {
        addIssue("Low", "Deposit is not mirrored in transactions", rawKey || deposit.id, siteName(deposit), deposit.amount, "Open and re-save it on the Deposits page so the Financials dashboard sees it. (This report already counts it.)");
      }
    });

    const depositIds = new Set(deposits.map((d) => d.id));
    transactions.forEach((tx) => {
      if (tx.sourceCollection === "deposits" && tx.sourceId && !depositIds.has(tx.sourceId)) {
        addIssue("Medium", "Ledger row points to a deposit that was deleted", tx.id, siteName(tx), tx.amount, "Delete this transactions row (the deposit is gone). This report does not count it.");
      }
      if (!tx.sourceCollection && !tx.sourceId) {
        addIssue("Low", "Manual transaction with no source record", tx.id, firstValue(tx.vendor, tx.payer, siteName(tx)), tx.amount, "OK for manual entries. Make sure it is not also entered on Deposits/Expenses.");
      }
    });

    jobs.forEach((job) => {
      const key = jobNumber(job);
      if (isRecurring(job)) {
        const visit = recurringVisitInfo[job.id];
        if (!visit) return;
        const notInvoiced = [...visit.completedKeys].filter((dateKey) => !visit.isInvoiced(dateKey));
        if (notInvoiced.length) {
          const amount = notInvoiced.reduce((sum, dateKey) => sum + recurringVisitFee(job, dateKey), 0);
          addIssue("Medium", `${notInvoiced.length} completed visit(s) have no invoice`, key, siteName(job), amount, "Create the invoices from Send Invoices.");
        }
        return;
      }
      const isCompleted = lower(job.status) === "completed";
      if (isCompleted && !(maps.invoicesByJobId[job.id] || []).length) {
        addIssue("Medium", "Completed job has no approved invoice", key, siteName(job), firstMoneyValue(job.estimatedTotal, job.price), "Create the invoice from Send Invoices, or fix the job status.");
      }
    });

    const checkGroups = {};
    deposits.forEach((deposit) => {
      const check = normalizeKey(deposit.checkNumber);
      if (!check) return;
      const group = `${check}|${toDateKey(deposit.date)}|${toNum(deposit.amount)}`;
      checkGroups[group] = checkGroups[group] || [];
      checkGroups[group].push(deposit);
    });
    Object.values(checkGroups).forEach((group) => {
      if (group.length > 1 && !group.some((d) => d.splitCheck || d.checkGroupId)) {
        addIssue("Medium", "Same check / date / amount entered more than once", group[0].checkNumber, siteName(group[0]), group[0].amount, "Confirm it is a duplicate and delete one on the Deposits page.");
      }
    });

    // Tech Daily Pay checks
    laborRange.rows.forEach((row) => {
      if (!(row.dailyPay > 0)) {
        addIssue("Medium", "Tech worked but has $0 daily pay", `${row.date} ${row.name || row.userId}`, "", 0, "Set the Work Hourly Rate (or Daily Pay Rate) on the Staff page, then re-save that day on Tech Daily Pay.");
      }
      (row.jobIds || []).forEach((jobId) => {
        if (!maps.jobById[baseJobId(jobId)]) {
          addIssue("Low", "Tech Daily Pay points to a job that no longer exists", `${row.date} ${row.name || ""}`, "", row.dailyPay, "Re-pick the jobs for that day on Tech Daily Pay.");
        }
      });
    });
    if (laborRange.unassigned > 0) {
      addIssue("Low", "Tech Daily Pay not tied to any job", `${startDate} to ${effectiveEndDate}`, "", laborRange.unassigned, "Pick the jobs each tech worked on Tech Daily Pay so job profit is accurate.");
    }

    return rows;
  }, [approvedInvoices, invoiceBalances, deposits, transactions, jobs, recurringVisitInfo, laborRange, startDate, effectiveEndDate, maps]);

  /* ---------------------------------------------------------- summary */
  const summaryRows = useMemo(() => {
    const totalReceived = summarize(moneyReceivedRows, "Amount");
    const totalPaidOut = summarize(moneyPaidRows, "Amount");
    const totalOwedToYou = summarize(whoOwesMeRows, "Balance Due");
    const totalYouOwe = summarize(whoIOweRows, "Amount Owed");
    const totalPotential = summarize(potentialMoneyRows, "Amount");
    const totalJobBalance = summarize(jobProfitabilityRows, "Balance Due");
    const totalWarrantyCost = summarize(jobProfitabilityRows, "Warranty Cost");
    const totalEmployeePaid = payrollSubDetailRows
      .filter((row) => row["Pay Group"] === "Employee")
      .reduce((sum, row) => sum + toNum(row.Amount), 0);
    const totalSubcontractorPaid = payrollSubDetailRows
      .filter((row) => row["Pay Group"] === "Subcontractor")
      .reduce((sum, row) => sum + toNum(row.Amount), 0);

    return [
      { Metric: "Money Received", Amount: money(totalReceived), Notes: "Deposits + bulk income + other income in the date range (each dollar once)" },
      { Metric: "Money Paid Out", Amount: money(totalPaidOut), Notes: "Expenses, pay, bills, bank lines, materials in the date range (each dollar once)" },
      { Metric: "Employee Pay", Amount: money(totalEmployeePaid), Notes: "Employee pay actually paid (part of Money Paid Out)" },
      { Metric: "Subcontractor Pay", Amount: money(totalSubcontractorPaid), Notes: "Subcontractor pay actually paid (part of Money Paid Out)" },
      { Metric: "Net Cash", Amount: money(totalReceived - totalPaidOut), Notes: "Money received minus money paid out" },
      { Metric: "Tech Daily Pay Earned", Amount: money(laborRange.total), Notes: "Labor from the Tech Daily Pay page. Info only - NOT added to Money Paid Out" },
      { Metric: "Tech Daily Pay Not Tied to a Job", Amount: money(laborRange.unassigned), Notes: "Part of Tech Daily Pay Earned with no job picked" },
      { Metric: "Daily Pay Earned minus Employee Pay Paid", Amount: money(laborRange.total - totalEmployeePaid), Notes: "Positive = earned but not paid yet (or paid outside Employee Pay)" },
      { Metric: "Who Owes Me", Amount: money(totalOwedToYou), Notes: "Open invoice balances" },
      { Metric: "Who I Owe", Amount: money(totalYouOwe), Notes: `Unpaid bills/pay + scheduled overhead through ${effectiveLookAhead}` },
      { Metric: "Potential Money Coming In", Amount: money(totalPotential), Notes: `Work not invoiced yet: open proposals, jobs, recurring visits through ${effectiveLookAhead}` },
      { Metric: "Job Balance Due", Amount: money(totalJobBalance), Notes: "Invoiced/contract minus received, all jobs (overlaps Who Owes Me)" },
      { Metric: "Warranty Spend", Amount: money(totalWarrantyCost), Notes: "Cost spent on jobs marked as warranty" },
      { Metric: "Reconciliation Warnings", Amount: reconciliationRows.length, Notes: "See the Reconciliation sheet in the Excel file" },
    ];
  }, [
    moneyReceivedRows,
    moneyPaidRows,
    payrollSubDetailRows,
    whoOwesMeRows,
    whoIOweRows,
    potentialMoneyRows,
    jobProfitabilityRows,
    reconciliationRows,
    laborRange,
    effectiveLookAhead,
  ]);

  const exportMasterReport = () => {
    downloadWorkbook(
      {
        Summary: summaryRows,
        "Money Received": moneyReceivedRows,
        "Money Paid Out": moneyPaidRows,
        "Tech Daily Pay": techDailyPayRows,
        "Tech Daily Pay by Person": techDailyPayByPersonRows,
        "Tech Daily Pay by Job": techDailyPayByJobRows,
        "Pay Detail": payrollSubDetailRows,
        "Pay Monthly Summary": payrollSubMonthlySummaryRows,
        "Pay Yearly Summary": payrollSubYearlySummaryRows,
        "Pay Grand Summary": payrollSubGrandSummaryRows,
        "Scheduled Overhead": paidOut.scheduledRows,
        "Daily Spending": paidOut.dailySpendingRows,
        "Bank Statement Audit": bankStatementAuditRows,
        "Who Owes Me": whoOwesMeRows,
        "Who I Owe": whoIOweRows,
        "Potential Income": potentialMoneyRows,
        "Job Profitability": jobProfitabilityRows,
        Reconciliation: reconciliationRows,
      },
      `Priority_Pest_Defense_Accounting_${startDate}_to_${effectiveEndDate}.xlsx`
    );
  };

  const count = (key) => (data[key] || []).length;
  const dataLoadedRows = [
    ["approvedInvoices", count("approvedInvoices"), "Who Owes Me, invoice balances, job invoiced totals"],
    ["deposits", count("deposits"), "Money Received, invoice payments"],
    ["transactions", count("transactions"), "Ledger income/expense (mirrors of Deposits, Expenses, Bank Statement)"],
    ["bankStatementEntries", count("bankStatementEntries"), "Bank Statement Audit; counted through transactions"],
    ["jobs (one-time + recurring)", count("jobs"), "Job profitability, potential income, recurring visits"],
    ["job_totals", count("jobTotals"), "Job totals lookup"],
    ["proposals", count("proposals"), "Potential income"],
    ["dailyLabor (Tech Daily Pay)", count("laborDays"), "Tech Daily Pay sheets, job labor (actual)"],
    ["users (Staff)", count("users"), "Tech names and pay rates"],
    ["employeePay", count("employeePay"), "Money Paid Out / Pay Detail / Who I Owe"],
    ["subcontractorPay", count("subcontractorPay"), "Money Paid Out / Pay Detail / job sub pay"],
    ["subcontractorPayments (older)", count("subcontractorPayments"), "Money Paid Out / job sub pay"],
    ["monthlyBills", count("monthlyBills"), "Money Paid Out / Who I Owe"],
    ["bankDeductions", count("bankDeductions"), "Money Paid Out"],
    ["monthly_expenses", count("monthlyExpenses"), "Scheduled overhead / Who I Owe"],
    ["bulk_material_expenses", count("bulkMaterialExpenses"), "Money Paid Out"],
    ["bulk_income", count("bulkIncome"), "Money Received / job received"],
    ["dailySpending", count("dailySpending"), "Money Paid Out (months without a bank statement)"],
    ["job payments / expenses", count("jobPayments") + count("jobExpenses"), "Money Received / Paid Out, job profit"],
  ];

  const previewRows = [
    ["Money Received", moneyReceivedRows.length, summarize(moneyReceivedRows, "Amount")],
    ["Money Paid Out", moneyPaidRows.length, summarize(moneyPaidRows, "Amount")],
    ["Tech Daily Pay (one row per tech per day)", techDailyPayRows.length, laborRange.total],
    ["Employee / Sub Pay Detail", payrollSubDetailRows.length, summarize(payrollSubDetailRows, "Amount")],
    ["Scheduled Overhead", paidOut.scheduledRows.length, summarize(paidOut.scheduledRows, "Amount")],
    ["Daily Spending log", paidOut.dailySpendingRows.length, summarize(paidOut.dailySpendingRows, "Amount")],
    ["Bank Statement Audit", bankStatementAuditRows.length, null],
    ["Who Owes Me", whoOwesMeRows.length, summarize(whoOwesMeRows, "Balance Due")],
    ["Who I Owe", whoIOweRows.length, summarize(whoIOweRows, "Amount Owed")],
    ["Potential Income", potentialMoneyRows.length, summarize(potentialMoneyRows, "Amount")],
    ["Job Profitability", jobProfitabilityRows.length, null],
    ["Reconciliation Warnings", reconciliationRows.length, null],
  ];

  return (
    <div style={S.page}>
      <style>{`@keyframes acct-export-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <h2 style={{ margin: "0 0 6px" }}>Accounting Master Report</h2>
      <div style={{ color: "#64748b", fontSize: 14 }}>Priority Pest Defense LLC</div>

      <div style={S.note}>
        <strong>Read only.</strong> This report never changes invoices, deposits, jobs, pay, or bank entries. It reads
        everything entered on Approved Invoices, Deposits, Expenses, Bank Statement, Job Estimate, Daily Spending, Staff,
        and Tech Daily Pay, and puts it in one Excel file. Each dollar is counted once: ledger copies of deposits and pay
        are skipped, and Tech Daily Pay is shown as labor earned (not as money paid out) so it is never added on top of
        payroll.
      </div>

      {loadError && (
        <div style={{ ...S.note, color: "#991b1b", background: "#fef2f2", borderColor: "#fca5a5" }}>{loadError}</div>
      )}

      <div style={S.controls}>
        <label style={S.label}>
          Start Date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={S.input} />
        </label>
        <label style={S.label}>
          End Date (blank = today)
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={S.input} />
        </label>
        <label style={S.label}>
          Look ahead through
          <input type="date" value={lookAheadDate} onChange={(e) => setLookAheadDate(e.target.value)} style={S.input} />
        </label>
        <button type="button" onClick={loadData} disabled={loading} style={S.btn("#2563eb", loading)}>
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Spinner color="#fff" /> Refreshing...
            </span>
          ) : (
            "Refresh Data"
          )}
        </button>
        <button type="button" onClick={exportMasterReport} disabled={loading} style={S.btn("#15803d", loading)}>
          Download Excel Report
        </button>
      </div>

      <div style={S.cards}>
        {summaryRows.map((row) => (
          <div key={row.Metric} style={S.card}>
            <div style={{ fontSize: 13, color: "#475569" }}>{row.Metric}</div>
            <div style={{ fontSize: 22, fontWeight: 800, margin: "4px 0" }}>
              {row.Metric === "Reconciliation Warnings" ? row.Amount : displayMoney(row.Amount)}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{row.Notes}</div>
          </div>
        ))}
      </div>

      <h3>Report Preview</h3>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Excel Sheet</th>
              <th style={S.th}>Rows</th>
              <th style={S.th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.map(([label, rows, total]) => (
              <tr key={label}>
                <td style={S.td}>{label}</td>
                <td style={S.td}>{rows}</td>
                <td style={S.td}>{total === null ? "" : displayMoney(total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Data Loaded</h3>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Collection / Source</th>
              <th style={S.th}>Records</th>
              <th style={S.th}>Used For</th>
            </tr>
          </thead>
          <tbody>
            {dataLoadedRows.map(([name, records, usedFor]) => (
              <tr key={name}>
                <td style={S.td}>{name}</td>
                <td style={S.td}>{records}</td>
                <td style={S.td}>{usedFor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
